import { Typography } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import MinimizeSVG from "../../../assets/svgs/minimize.svg";
import PopupPlusSVG from "../../../assets/svgs/popup-plus.svg";
import styles from "./SelectableCard.module.scss";
import InfoIcon from "../../icon/InfoIcon";
import HtmlTooltip from "../../shared/HtmlTooltip";
import CardTooltipContent from "../card/CardTooltipContent";
import { IconButton } from "@mui/material";
import ReportButton from "../../shared/ReportButton";
import classNames from "classnames";
import CommonIconButtons from "../card/RightAction";

const SelectableCard = ({
	children,
	flex,
	title,
	minimizable,
	disabled,
	handleOnclickCard,
	selected,
	page = "",
	tooltipContent,
	handleOnClickAction,
	loading = false,
	handleOnClickReport,
	handleOnClickShareReport,
	pageType,
	isLayoutMode = false,
	isMobile = false,
	lastCard = false
}) => {
	const [showDetails, setShowDetails] = useState(true);	

	const handleClickOnDetailsSelectCard = useCallback(
		(totalTitle, defaultTab) => {
			if (!loading) {
				handleOnClickAction(totalTitle, defaultTab);
			}
		},
		[handleOnClickAction, loading]
	);

	const [isAnimating, setIsAnimating] = useState(false);
	const timerRef = useRef(null);

	const handleAnimationChange = useCallback(async () => {
		setIsAnimating(true);
		await new Promise(resolve => setTimeout(resolve, 0)); // Ensure state update

		if (timerRef.current) {
			clearTimeout(timerRef.current);
		}

		const stopAnimation = () => {
			setIsAnimating(false);
			timerRef.current = null;
		};

		if (loading) {
			const checkLoading = () => {
				if (!loading) {
					stopAnimation();
				} else {
					requestAnimationFrame(checkLoading); // Continue checking
				}
			};
			requestAnimationFrame(checkLoading); // Start checking
		} else {
			timerRef.current = setTimeout(stopAnimation, 500); // Minimum 1000ms
		}
	}, [loading]);

	useEffect(() => {
		return () => {
			if (timerRef.current) {
				clearTimeout(timerRef.current);
			}
		};
	}, []);

	const handleOnclickCardSelect = useCallback(
		(e) => {
			handleAnimationChange()
			e.stopPropagation();
			if (!loading) {
				handleOnclickCard();
			}
		},
		[handleAnimationChange, handleOnclickCard, loading]
	);

	return (
		<div
			className={classNames(
				styles.selectableCard,
				disabled && styles.disabledCard,
				selected && styles.selected,
				page && styles[page],
				loading && styles.skeleton,
				isAnimating && styles.animating,
				flex && styles.flex,
				lastCard && styles.lastCard
			)}
			onClick={handleOnclickCardSelect}
		>
			<div className={`df aic ${styles.cardHeader}`}>
				<div className={`df aic ${styles.left}`}>
					<Typography
						variant="subtitle2"
						sx={{
							fontSize: isMobile ? 12 : 15,
							fontWeight: isMobile ? 800 : 'bold',
							...!isMobile && {
								maxWidth: "180px",
								whiteSpace: "normal",
								overflow: "hidden",
								textOverflow: "ellipsis",
								display: "-webkit-box",
								WebkitLineClamp: 2,
								WebkitBoxOrient: "vertical",
							},
							'@media (max-width: 1024px)': {
								maxWidth: "140px",
								fontSize: 14,
							},
							'@media (max-width: 768px)': {
								maxWidth: "100px",
								fontSize: 12,
							},
						}}
					>
						{title}
					</Typography>
				</div>
				{!isLayoutMode ? (
					<div className={`df aic ${styles.right}`}>
						<div className={styles.tooltipWrapper}>
							{tooltipContent && (
								<div className={styles.htmlTooltip}>
									<HtmlTooltip content={!loading && <CardTooltipContent content={tooltipContent} />}>
										<IconButton
											disableFocusRipple={true}
											disableRipple={true}
											sx={{
												cursor: loading ? "default" : "pointer",
												'@media (max-width: 768px)': {
													width: 18,
													height: 18,
													padding: 0,
													paddingLeft: 0.4,
												}
											}}
										>
											<InfoIcon />
										</IconButton>
									</HtmlTooltip>
								</div>
							)}
							{loading ? (
								title !== "Total +/-" && (
									<div
										style={{ width: "24px", height: "24px", borderRadius: "50%", marginRight: "7px" }}
										className="skeleton"
									/>
								)
							) : (
								<ReportButton
									handleOnClickReport={handleOnClickReport}
									handleOnClickShareReport={handleOnClickShareReport}
									loading={loading}
									title={title}
									sx={{
										width: isMobile ? 21 : 23,
										height: isMobile ? 21 : 23,
									}}
									openFrom="card"
									pageType={pageType}
									isCustomAlertEnable={false}
								/>
							)}
							{handleOnClickAction && (
								<CommonIconButtons
									title={title}
									handleOnClickAction={(title, defaultTab) => handleClickOnDetailsSelectCard(title, defaultTab)}
									loading={loading}
									isMobile={isMobile}
								/>
							)}
						</div>
						{minimizable ? (
							<div
								className={`p-l-9 p-r-5 p-t-5 p-b-5 m-l-15`}
								style={{ cursor: "pointer" }}
								onClick={() => setShowDetails(!showDetails)}
							>
								{showDetails ? <MinimizeSVG /> : <PopupPlusSVG />}
							</div>
						) : undefined}
					</div>
				) : (
					<div className={`df aic ${styles.right}`}>
						<div className={styles.tooltipWrapper}>
							{tooltipContent && (
								<div className={styles.htmlTooltip}>
									<HtmlTooltip content={!loading && <CardTooltipContent content={tooltipContent} />}>
										<IconButton
											disableFocusRipple={true}
											disableRipple={true}
											sx={{ cursor: loading ? "default" : "pointer" }}
										>
											<InfoIcon />
										</IconButton>
									</HtmlTooltip>
								</div>
							)}
							{loading ? (
								title !== "Total +/-" && (
									<div
										style={{ width: "24px", height: "24px", borderRadius: "50%", marginRight: "7px" }}
										className="skeleton"
									/>
								)
							) : (
								<ReportButton
									handleOnClickReport={handleOnClickReport}
									handleOnClickShareReport={handleOnClickShareReport}
									loading={loading}
									title={title}
									sx={{
										width: 23,
										height: 23,
									}}
									openFrom="card"
									pageType={pageType}
									isCustomAlertEnable={false}
									isMobile={isMobile}
								/>
							)}
							{handleOnClickAction && (
								<CommonIconButtons
									title={title}
									handleOnClickAction={(title, defaultTab) => handleClickOnDetailsSelectCard(title, defaultTab)}
									loading={loading}
									isMobile={isMobile}
								/>)
							}
						</div>
						{minimizable ? (
							<div
								className={`p-l-9 p-r-5 p-t-5 p-b-5 m-l-15`}
								style={{ cursor: "pointer" }}
								onClick={() => setShowDetails(!showDetails)}
							>
								{showDetails ? <MinimizeSVG /> : <PopupPlusSVG />}
							</div>
						) : undefined}
					</div>
				)}
			</div>
			{showDetails ? <div className={`df aic ${styles.cardContent}`}>{children}</div> : undefined}
		</div>
	);
};

export default SelectableCard;
